import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import { Panel } from 'components/Panel'
import { DashboardConfigProps } from 'interfaces/navigationPage.interface'
import { useMemo, useState } from 'react'
import useIsMobile from 'utils/useIsMobile'
import { USDTransformFormat } from 'config/common/transformValue/money'
import { mean } from 'lodash'
import MapHeatmap from '../MapHeatmap'
import KeyPieCharts from '../Shared/KeyPieCharts'
import ProgressChart from '../Shared/ProgressChart'
import { StatCard } from '../Shared/StatCard'
import QuantityBarChart from './QuantityBarChart'
import { procurementOptions } from './util'
import PricePerDoseChart from './PricePerDoseChart'
import Header from '../IDATrialsMapDashboard/Header'
import { createPortal } from 'react-dom'
import { compactNumberWithPrefix } from 'utils/formatNumber'

export interface InputObject {
  [key: string]: any
}

export default function ProcurementMapDashboard({
  chartData,
  loaded,
}: DashboardConfigProps) {
  const [isMobile] = useIsMobile()

  const [stepIndex, setStepIndex] = useState<number>(0)
  const [stepsEnabled, setStepsEnabled] = useState(
    localStorage.getItem('trialsDashboardTourCompleted') !== 'true' &&
      !isMobile &&
      window.location.hostname !== 'localhost'
  )

  const hasData = chartData && chartData.length > 0

  const uniqueCandidates = useMemo(
    () => [...new Set(chartData.flatMap((item) => item.candidates))],
    [chartData]
  )
  const meanPrice = useMemo(
    () =>
      mean(
        chartData
          .flatMap((item) => parseFloat(item.pricePerUnitUsd as string))
          .filter((x) => !Number.isNaN(x))
      ),
    [chartData]
  )

  const totalVolume = useMemo(
    () =>
      chartData
        .map((x) => (x.quantity ?? 0) as number)
        .reduce((acc, val) => acc + val, 0),
    [chartData]
  )

  return (
    <>
      <Box height='100%' id='divToPrint' display={'flex'}>
        <Panel
          height='100%'
          width='65%'
          border='1px solid'
          borderColor='gray3'
          background='white'
          borderRadius='8px'
          flex={1}
          p={2}
          position={'relative'}
        >
          {document.getElementById('mapTrialsPortal') &&
            createPortal(
              <Header
                dataLength={chartData.length}
                setStepIndex={setStepIndex}
                setStepsEnabled={setStepsEnabled}
              />,
              document.getElementById('mapTrialsPortal') as Element
            )}
          {hasData ? (
            <Flex
              pointerEvents={'none'}
              zIndex={50}
              justifyContent={'space-between'}
              w='full'
              h='100%'
              gap={2}
              overflow='hidden'
            >
              <Flex
                pointerEvents={'auto'}
                justifyContent={'space-between'}
                flexDir={'column'}
                gap={2}
                maxW='350px'
                flexShrink={0}
                overflowY='auto'
              >
                <StatCard
                  titleProps={{ fontSize: 'xs' }}
                  valueProps={{ fontSize: 'sm' }}
                  title='Total Volume'
                  value={compactNumberWithPrefix(totalVolume, 3)}
                />
                <StatCard
                  titleProps={{ fontSize: 'xs' }}
                  valueProps={{ fontSize: 'sm' }}
                  title='Unique Candidates'
                  value={uniqueCandidates.length}
                />
                <StatCard
                  titleProps={{ fontSize: 'xs' }}
                  valueProps={{ fontSize: 'sm' }}
                  title='Mean Price'
                  value={
                    meanPrice ? USDTransformFormat(meanPrice.toString()) : 'N/A'
                  }
                />
                <Flex
                  gap={2}
                  className='keyPieCharts'
                  flexDir={'column'}
                  maxH='180px'
                >
                  <KeyPieCharts
                    multipleSelection={true}
                    title='Deals by'
                    data={chartData}
                    options={procurementOptions}
                    loading={!loaded}
                  />
                </Flex>
                <Box maxH='180px'>
                  <ProgressChart
                    loading={!loaded}
                    data={chartData}
                    options={procurementOptions}
                    title='Deals by'
                  />
                </Box>
              </Flex>

              <Box
                pointerEvents={'auto'}
                className='mapContainer'
                w='full'
                bg='white'
                height={'100%'}
              >
                <MapHeatmap
                  data={chartData}
                  summaryTitle='Total deals'
                  popoverTitle='Deals'
                  countFunction={(item: any) => item.quantity ?? 0}
                  itemToCount='recipients'
                  hideSummary
                  filterKey='recipients'
                />
              </Box>

              <Flex
                h='full'
                pointerEvents={'auto'}
                justifyContent={'space-between'}
                flexDir={'column'}
                gap={2}
              >
                <QuantityBarChart data={chartData} loading={!loaded} />
                <PricePerDoseChart data={chartData} loading={!loaded} />
              </Flex>
            </Flex>
          ) : (
            <Box
              p={40}
              textAlign='center'
              display='flex'
              alignItems='center'
              justifyContent='center'
              flexDirection='column'
              data-cy='loading'
            >
              {loaded ? (
                <Text ml={2}> No data found, try adjusting the filters.</Text>
              ) : (
                <Spinner />
              )}
            </Box>
          )}
        </Panel>
      </Box>
    </>
  )
}
